import { loadChooseServicesPage } from '../booking/pages/choose-services';
import { type BookingContext } from './booking-context';

export function bindSummary(context: BookingContext) {
	const toggleSummary = () => {
		context.root.querySelectorAll('.booking-summary__toggle').forEach((toggle) => {
			const isOpen = !toggle.classList.contains('booking-summary__toggle--active');

			toggle.classList.toggle('booking-summary__toggle--active', isOpen);
			toggle
				.querySelectorAll('.booking-summary__toggle-icon')
				.forEach((toggleIcon) => toggleIcon.classList.toggle('booking-summary__toggle-icon--active', isOpen));
			toggle.parentElement
				?.querySelectorAll('.booking-summary__details')
				.forEach((details) => details.classList.toggle('booking-summary__details--active', isOpen));
			toggle.parentElement?.classList.toggle('booking-summary__container--active', isOpen);

			document
				.querySelectorAll('.booking-summary__toggle-overlay')
				.forEach((overlay) => overlay.classList.toggle('booking-summary__toggle-overlay--active', isOpen));
		});
	};

	document.querySelectorAll('.booking-summary__toggle-overlay').forEach((overlay) => overlay.remove());

	// Add a new overlay
	const overlay = document.createElement('div');
	overlay.classList.add('booking-summary__toggle-overlay');

	overlay.addEventListener('click', toggleSummary);

	document.body.appendChild(overlay);

	context.root.querySelectorAll('.booking-summary__toggle').forEach((toggle) => toggle.addEventListener('click', toggleSummary));

	context.root.querySelector('.booking-summary__change-treatments')?.addEventListener('click', (e) => {
		if (context.data.book) {
			window.location.reload();
			return;
		}

		loadChooseServicesPage(context);
	});
}

export function updateSummary(context: BookingContext, rerenderRows: any = null) {
	// ...

	if (!rerenderRows) {
		rerenderRows = () => {
			if (context.data.serviceIds.length === 0) {
				loadChooseServicesPage(context);
			}
			updateSummary(context);
		};
	}

	const summary = context.root.querySelector<HTMLDivElement>('[data-summary]');

	let timeSummary = 0;

	if (!summary) {
		console.log('No summary element found');
		return;
	}
	const clinicNameElement = summary.querySelector<HTMLDivElement>('[data-clinic-name]');
	if (clinicNameElement) {
		clinicNameElement.textContent = context.data.locationName;
	}

	const dateElement = summary.querySelector<HTMLDivElement>('[data-date]');
	if (dateElement) {
		dateElement.textContent = context.data.displayDate ?? 'Please select a date';
	}

	const timeElement = summary.querySelector<HTMLDivElement>('[data-time]');
	if (timeElement) {
		timeElement.textContent = context.data.displayTime ?? 'Please select a time';
	}

	//
	// Services
	//
	const servicesElement = summary.querySelector<HTMLDivElement>('[data-services]');
	if (!servicesElement) {
		console.log('No services element found');
		return;
	}

	const serviceTemplate = servicesElement.querySelector<HTMLTemplateElement>('[data-service-template]');
	if (!serviceTemplate) {
		console.log('No service template found');
		return;
	}

	// delete all the children in servicesElement
	servicesElement.querySelectorAll('div').forEach((div) => div.remove());

	for (const [id, service] of context.data.serviceData.entries()) {
		timeSummary += service.duration;

		const serviceElement = serviceTemplate.content.cloneNode(true) as HTMLDivElement;

		const nameElement = serviceElement.querySelector('[data-name]');
		if (nameElement) nameElement.textContent = service.name;
		const durationElement = serviceElement.querySelector('[data-duration]');
		if (durationElement) durationElement.textContent = `${service.duration}`;
		const categoryElement = serviceElement.querySelector('[data-category]');
		if (categoryElement) categoryElement.textContent = service.subcategory;

		const removeButton = serviceElement.querySelector<HTMLButtonElement>('[data-remove-item]');
		if (removeButton) {
			if (context.data.reschedule) {
				removeButton.remove();
			} else {
				removeButton.addEventListener('click', () => {
					context.data.serviceIds = context.data.serviceIds.filter((x) => x !== id);
					context.data.serviceData.delete(id);
					rerenderRows?.();
				});
			}
		}

		servicesElement.appendChild(serviceElement);
	}

	//
	// Addons
	//
	const addonsElement = summary.querySelector<HTMLDivElement>('[data-addons]');
	if (!addonsElement) {
		console.log('addonsElement not found');
		return;
	}

	const addonTemplate = addonsElement.querySelector<HTMLTemplateElement>('[data-addon-template]');
	if (!addonTemplate) {
		console.log('addonTemplate not found');
		return;
	}

	addonsElement.querySelectorAll('div').forEach((div) => div.remove());

	for (const [id, addon] of context.data.addonData.entries()) {
		timeSummary += addon.duration;

		const addonElement = addonTemplate.content.cloneNode(true) as HTMLDivElement;

		const nameElement = addonElement.querySelector('[data-name]');
		if (nameElement) nameElement.textContent = addon.name;
		const durationElement = addonElement.querySelector('[data-duration]');
		if (durationElement) durationElement.textContent = `${addon.duration}`;
		const categoryElement = addonElement.querySelector('[data-category]');
		if (categoryElement) categoryElement.textContent = addon.subcategory;

		const removeButton = addonElement.querySelector<HTMLButtonElement>('[data-remove-item]');
		if (removeButton) {
			if (context.data.reschedule) {
				removeButton.remove();
			} else {
				removeButton.addEventListener('click', () => {
					context.data.addonIds = context.data.addonIds.filter((x) => x !== id);
					context.data.addonData.delete(id);
					rerenderRows();
				});
			}
		}

		addonsElement.appendChild(addonElement);
	}

	//
	// Consults
	//
	const consultsElement = summary.querySelector<HTMLDivElement>('[data-consults]');
	if (!consultsElement) {
		console.log('no consults element');
		return;
	}

	const consultTemplate = consultsElement.querySelector<HTMLTemplateElement>('[data-consult-template]');
	if (!consultTemplate) {
		console.log('no consult template');
		return;
	}

	consultsElement.querySelectorAll('div').forEach((div) => div.remove());

	if (context.data.includeConsult && context.data.consultData.size === 0) {
		console.log('adding consult');
		const consultElement = consultTemplate.content.cloneNode(true) as HTMLDivElement;

		const nameElement = consultElement.querySelector('[data-name]');
		if (nameElement)
			nameElement.textContent = `As a first time client, we've added a complimentary consult to your booking to ensure your suitability and to answer any questions you may have about your treatment.`;
		const durationElement = consultElement.querySelector('[data-duration]');
		if (durationElement) durationElement.textContent = ``;
		const categoryElement = consultElement.querySelector('[data-category]');
		if (categoryElement) categoryElement.textContent = ``;

		const removeButton = consultElement.querySelector<HTMLButtonElement>('[data-remove-item]');
		if (removeButton) {
			removeButton.remove();
		}

		consultsElement.appendChild(consultElement);
	}

	for (const [id, consult] of context.data.consultData.entries()) {
		timeSummary += consult.duration;

		const consultElement = consultTemplate.content.cloneNode(true) as HTMLDivElement;

		const nameElement = consultElement.querySelector('[data-name]');
		if (nameElement) nameElement.textContent = consult.name;
		const durationElement = consultElement.querySelector('[data-duration]');
		if (durationElement) durationElement.textContent = `${consult.duration}`;
		const categoryElement = consultElement.querySelector('[data-category]');
		if (categoryElement) categoryElement.textContent = consult.subcategory;

		const removeButton = consultElement.querySelector<HTMLButtonElement>('[data-remove-item]');
		if (removeButton) {
			removeButton.remove();
		}

		consultsElement.appendChild(consultElement);
	}

	const timeSummaryElement = summary.querySelector('[data-total-time]');
	if (timeSummaryElement) {
		timeSummaryElement.textContent = `${timeSummary} mins`;
	}
}
